;globalThis["_sentryRewritesTunnelPath"] = "/sntrytnl";globalThis["SENTRY_RELEASE"] = {"id":"hom-frontend@1.137.13-a029562"};globalThis["_sentryBasePath"] = undefined;globalThis["_sentryNextJsVersion"] = "15.4.10";globalThis["_sentryRewriteFramesAssetPrefixPath"] = "";globalThis["_sentryAssetPrefix"] = undefined;globalThis["_sentryExperimentalThirdPartyOriginStackFrames"] = undefined;globalThis["_sentryRouteManifest"] = "{\"isrRoutes\":[],\"dynamicRoutes\":[],\"staticRoutes\":[]}";import * as Sentry from '@sentry/nextjs'
import { posthog } from 'posthog-js'

import appConfig from '~/config/app'
import { maskReplayText, maskReplayUrl } from '~/utils/analytics/sessionReplayMasking.utils'

import { parseSampleRate } from './sentry.utils'
import { POSTHOG_BOOTSTRAP_COOKIE_KEY, type PosthogBootstrapPayload, type PosthogSuperProperties } from './middleware'

// Shared between Sentry's `ignoreErrors` and PostHog's `before_send` so both error trackers
// suppress the same category of noise (browser/network failures with no actionable stack).
const IGNORED_ERROR_PATTERNS: Array<string | RegExp> = [
  'ResizeObserver loop',
  'ResizeObserver loop completed with undelivered notifications',
  'Non-Error promise rejection',
  'Load failed',
  'Failed to fetch',
  'NetworkError',
  'AbortError',
  'Script error.',
  /ChunkLoadError/,
  /Loading chunk .* failed/,
  'fb_xd_fragment',
  'top.GLOBALS',
  // Thrown by third-party/extension scripts and ad-network in-app browsers (e.g. Outbrain landing
  // traffic), not by our code — captured via window.onerror with no actionable stack. Covers the
  // V8/JSC ("Right-hand side of 'instanceof' is not an object/callable") and SpiderMonkey
  // ("invalid 'instanceof' operand") phrasings.
  /[Rr]ight[- ]hand side of '?instanceof'? is not (an object|callable)/,
  /invalid 'instanceof' operand/,
]

const matchesIgnoredErrorPattern = (message: string): boolean =>
  IGNORED_ERROR_PATTERNS.some((pattern) =>
    typeof pattern === 'string' ? message.includes(pattern) : pattern.test(message),
  )

Sentry.init({
  dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
  environment: `${appConfig.isMobileApp ? 'mobile-' : ''}${appConfig.environment}`,
  release: process.env.NEXT_PUBLIC_SENTRY_RELEASE,
  normalizeDepth: 5,

  // Sentry's session replay is off in the school platform; replay there is PostHog's, with the
  // masking set up in `posthog.init` below. Dropping the
  // integration, not just zeroing the rates below, also keeps the replay bundle from loading. The
  // array is merged with Sentry's defaults, so the empty branch keeps everything else intact.
  integrations: appConfig.isSchoolPlatform
    ? []
    : [
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],

  tracesSampleRate: parseSampleRate(process.env.NEXT_PUBLIC_SENTRY_TRACES_SAMPLE_RATE, 0),
  // Belt and braces: whatever NEXT_PUBLIC_SENTRY_REPLAYS_* hold, replay stays at 0 for schools.
  replaysSessionSampleRate: appConfig.isSchoolPlatform
    ? 0
    : parseSampleRate(process.env.NEXT_PUBLIC_SENTRY_REPLAYS_SESSION_SAMPLE_RATE, 0),
  replaysOnErrorSampleRate: appConfig.isSchoolPlatform
    ? 0
    : parseSampleRate(process.env.NEXT_PUBLIC_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE, 0),

  beforeSend(event) {
    if (event.request?.cookies) delete event.request.cookies
    if (event.request?.headers) delete event.request.headers
    return event
  },

  ignoreErrors: IGNORED_ERROR_PATTERNS,

  denyUrls: [
    /extensions\//i,
    /^chrome:\/\//i,
    /^moz-extension:\/\//i,
    /graph\.facebook\.com/i,
    /connect\.facebook\.net/i,
  ],

  enabled: ['staging', 'qa', 'beta', 'alpha', 'prod', 'development'].includes(appConfig.environment),
})

type ConsentEvent = CustomEvent<{
  consents: {
    cookie_cat_necessary: true
    cookie_cat_functional: boolean | null
    cookie_cat_statistic: boolean | null
    cookie_cat_marketing: boolean | null
    cookie_cat_unclassified: boolean | null
  }
}>

// Functional since it may be used for feature flags, surveys etc
const POSTHOG_COOKIE_CATEGORY = 'cookie_cat_functional'

interface ParsedBootstrapCookie {
  distinctID?: string
  isIdentifiedID?: boolean
  featureFlags?: Record<string, string | boolean>
  superProperties?: PosthogSuperProperties
}

const getPosthogBootstrapData = (): ParsedBootstrapCookie => {
  if (typeof document === 'undefined') return {}

  const cookieString = document.cookie
    .split('; ')
    .find((cookie) => cookie.startsWith(`${POSTHOG_BOOTSTRAP_COOKIE_KEY}=`))

  if (!cookieString) return {}

  try {
    const cookieValue = decodeURIComponent(cookieString.split('=').slice(1).join('='))
    const parsedCookie = JSON.parse(cookieValue) as Partial<PosthogBootstrapPayload>

    // Filter out `false` values — PostHog bootstrap with `false` means the hook returns `undefined`
    // instead of allowing the client to re-fetch the real evaluation from the server.
    const featureFlags = parsedCookie.featureFlags
      ? Object.fromEntries(Object.entries(parsedCookie.featureFlags).filter(([, v]) => v !== false))
      : undefined

    return {
      distinctID: parsedCookie.distinctID,
      isIdentifiedID: parsedCookie.isIdentifiedID,
      featureFlags,
      superProperties: parsedCookie.superProperties,
    }
  } catch {
    return {}
  }
}

const isConsentEvent = (subject: unknown): subject is ConsentEvent =>
  subject instanceof CustomEvent &&
  typeof subject.detail === 'object' &&
  subject.detail !== null &&
  typeof subject.detail.consents === 'object' &&
  subject.detail.consents !== null

// posthog-js keeps its state (distinct_id, $device_id, the $sesid session id) under this key in
// localStorage, mirrored into a cookie of the same name — and the cookie is the only copy the
// edge middleware can read. On iOS Safari the cookie routinely goes missing while localStorage
// stays intact (ITP expires script-set cookies after 7 days, and cookie-blocking setups do the
// same), so the middleware mints a fresh UUID for a browser that already has an identity.
const hasOwnPosthogState = (token: string): boolean => {
  if (typeof window === 'undefined') return false
  const storageKey = `ph_${token}_posthog`
  try {
    if (window.localStorage.getItem(storageKey)) return true
  } catch {
    // localStorage can be unavailable (privacy modes) — fall through to the cookie check
  }
  return document.cookie.split('; ').some((cookie) => cookie.startsWith(`${storageKey}=`))
}

// Consent decision persisted by posthog-js ('1' granted / '0' denied; localStorage by default,
// historically a cookie). A visitor who denied consent captures nothing today (cookieless_mode
// was removed from this init on 2026-05-07), so skipping the bootstrap id is inert for them —
// but if cookieless_mode 'on_reject' ever returns, a bootstrapped UUID would replace the
// $posthog_cookieless sentinel (bootstrap register()s over it, the sentinel is register_once)
// and defeat PostHog's server-side dedup. Guard now so that mode stays safe to re-enable.
const isPosthogConsentDenied = (token: string): boolean => {
  if (typeof window === 'undefined') return false
  const consentKey = `__ph_opt_in_out_${token}`
  let value: string | null = null
  try {
    value = window.localStorage.getItem(consentKey)
  } catch {
    // localStorage can be unavailable (privacy modes) — fall through to the cookie check
  }
  if (value === null) {
    value =
      document.cookie
        .split('; ')
        .find((cookie) => cookie.startsWith(`${consentKey}=`))
        ?.split('=')[1] ?? null
  }
  // The values posthog-js's isNoLike() treats as a denial (it only ever writes '0' itself).
  return value !== null && ['0', 'false', 'no'].includes(value)
}

if (process.env.NEXT_PUBLIC_POSTHOG_KEY) {
  const posthogToken = process.env.NEXT_PUBLIC_POSTHOG_KEY
  const { superProperties, distinctID, isIdentifiedID, featureFlags } = getPosthogBootstrapData()

  // Adopt the middleware-minted identity only in a browser posthog-js has never seen. Bootstrap
  // ids are register()ed OVER whatever persistence restored (posthog-core.js), so in a browser
  // with existing state the bootstrap id would overwrite the real identity on every homepage
  // view — each cookie-less pageview became a brand-new "person" while the session id survived
  // in localStorage (hundreds of phantom one-pageview persons per day sharing one session).
  // Feature flags are always safe to bootstrap: they carry the variant the server just rendered.
  const adoptBootstrapIdentity =
    distinctID !== undefined && !hasOwnPosthogState(posthogToken) && !isPosthogConsentDenied(posthogToken)
  const bootstrap = adoptBootstrapIdentity ? { distinctID, isIdentifiedID, featureFlags } : { featureFlags }

  posthog.init(posthogToken, {
    // This configures posthog to send requests to `/ph` instead of to posthog directly
    // the requests are proxied to posthog thanks to the relevant rewrite rules in next.config.js
    api_host: '/ph',
    ui_host: process.env.NEXT_PUBLIC_POSTHOG_UI_HOST,
    defaults: '2026-01-30',
    // For an anonymous visitor posthog-js adopts `bootstrap.distinctID` as the client's $device_id
    // (posthog-core.js). The middleware sets bootstrap.distinctID to the same id it evaluated the
    // device_id-bucketed landing-hero flag with, so SSR and client resolve the same variant — without
    // a get_device_id override (re-adding one would route reset()'s new anon id through it and re-merge users).
    bootstrap,
    // Enable posthog site app in dev environments (for early access feature ui)
    opt_in_site_apps: !appConfig.isProdEnvironment,
    // The school platform keeps PostHog initialized for feature flags, the content-feedback survey
    // and masked session replay; everything else below is switched off. ⚠ `opt_out_capturing_by_default`
    // is only the INITIAL consent value — CookieConsentScript opts non-EEA visitors back in — so the
    // per-feature switches below are what actually holds, being re-read on every capture path.
    opt_out_capturing_by_default: appConfig.isSchoolPlatform,
    autocapture: !appConfig.isSchoolPlatform,
    // posthog-js only enables SPA (pushState/replaceState/popstate) pageview tracking when this is
    // the literal string 'history_change' — `isEnabled` in page-view-manager.js checks strict
    // equality against that string, so a boolean `true` here silently degrades to "one $pageview at
    // init only" and every client-side route change in this Pages Router app goes uncaptured.
    capture_pageview: appConfig.isSchoolPlatform ? false : 'history_change',
    capture_pageleave: !appConfig.isSchoolPlatform,
    // Which school sessions are recorded is set in the PostHog project (Replay → trigger groups).
    // What a recording may contain is set here: for each option below the client value wins over
    // the project's. Everything is masked except the exercise question and answer and the
    // content-feedback comment, which carry `data-ph-unmask`; elements whose attributes carry a name
    // carry `data-ph-block` (src/utils/analytics/sessionReplayMasking.utils.ts).
    disable_session_recording: false,
    enable_recording_console_log: appConfig.isSchoolPlatform ? false : undefined,
    session_recording: appConfig.isSchoolPlatform
      ? {
          maskAllInputs: true,
          maskTextSelector: '*',
          maskTextFn: maskReplayText,
          maskInputFn: maskReplayText,
          blockSelector: 'img, video, canvas, iframe, [data-ph-block]',
          captureCanvas: { recordCanvas: false },
          recordHeaders: false,
          recordBody: false,
          // The recorder also passes every recorded page URL through this, not only network requests.
          maskCapturedNetworkRequestFn: (request) => ({ ...request, name: maskReplayUrl(request.name) }),
        }
      : {},
    // `autocapture: false` does NOT cover these four — unset, each falls through to the PostHog
    // project's server-side config, so a UI toggle could start collecting with no code change.
    // `capture_exceptions` stays ON; `before_send` below depends on those events existing.
    capture_heatmaps: !appConfig.isSchoolPlatform,
    capture_performance: !appConfig.isSchoolPlatform,
    capture_dead_clicks: !appConfig.isSchoolPlatform,
    capture_exceptions: true,
    // Drop autocaptured exceptions matching the same noise patterns Sentry already ignores
    // (e.g. Safari/WebKit's generic 'Load failed' from fetch rejections). Keeps the PostHog
    // error tracking feed in sync with Sentry's `ignoreErrors`.
    before_send: (event) => {
      if (!event || event.event !== '$exception') return event

      const exceptionList = event.properties?.$exception_list
      if (!Array.isArray(exceptionList)) return event

      const isIgnored = exceptionList.some((exception) => {
        const value = typeof exception?.value === 'string' ? exception.value : ''
        const type = typeof exception?.type === 'string' ? exception.type : ''
        return matchesIgnoredErrorPattern(value) || matchesIgnoredErrorPattern(type)
      })

      return isIgnored ? null : event
    },
  })

  // The school platform has no cookie-consent component, so nothing else grants consent — and
  // posthog-js discards a survey's answers while opted out, though it still renders it.
  //
  // ⚠ After init(), not by flipping `opt_out_capturing_by_default` above: init() calls identify()
  // itself on the bootstrap `isIdentifiedID` path, and keeping capture off until it returns is what
  // suppresses that $identify (invariant 1 in docs/school-platform-tracking.md).
  if (appConfig.isSchoolPlatform) posthog.opt_in_capturing({ captureEventName: null })

  // Mirror Sentry's `environment` + `release` tags so PostHog exceptions are filterable by deploy
  // stage and release. Uses the same `mobile-` prefix and `NEXT_PUBLIC_APP_RELEASE` git-SHA tag.
  posthog.register({
    environment: `${appConfig.isMobileApp ? 'mobile-' : ''}${appConfig.environment}`,
    release: process.env.NEXT_PUBLIC_APP_RELEASE,
  })

  // Middleware decides which super properties to attach via the bootstrap cookie; registering
  // synchronously here means they ride the first $pageview (which posthog-js enqueues post-init).
  if (superProperties && Object.keys(superProperties).length > 0) {
    posthog.register_for_session(superProperties)
  }

  // Can't find any good docs for the CookieInformation implementation but apparently there is an eventhandler
  // according to at least the following artices:
  // https://support.cookieinformation.com/en/articles/5444629-block-third-party-cookies-with-a-script
  // https://support.cookieinformation.com/en/articles/8898876-set-up-cookie-information-consent-popup-in-shopify
  window?.addEventListener?.('CookieInformationConsentGiven', (event: unknown): void => {
    if (!isConsentEvent(event)) {
      posthog.captureException('CookieInformationConsentGiven event listener called with invalid parameters')

      // It's important to opt out here since posthog does not start capturing anything by default with `cookieless_mode: 'on_reject'`
      // Opting out makes posthog start capturing events in cookieless mode
      posthog.opt_out_capturing()
      return
    }

    const functionalCookiesConsent = event.detail.consents[POSTHOG_COOKIE_CATEGORY]

    if (functionalCookiesConsent === null) {
      // The consent for any category is only ever null when the cookie consent dialog is initially shown
      // and the user has not opted in or out of the category, meaning this event handler will be called
      // again with the correct value once the user makes a decision
      return
    } else if (functionalCookiesConsent) {
      posthog.opt_in_capturing()
    } else {
      posthog.opt_out_capturing()
    }
  })
}

export const onRouterTransitionStart = Sentry.captureRouterTransitionStart
